import { MODEL_METADATA, ModelType } from "@aix/apiutils/src/models/pricing"
import { GeminiModelType } from "@aix/apiutils/src/llms/enums"
import type { DropResult } from "@hello-pangea/dnd"
import { DragDropContext, Droppable } from "@hello-pangea/dnd"
import { useNavigate } from "@tanstack/react-router"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import BrainChatbox from "../components/BrainChatbox"
import MarkdownEditor from "../components/MarkdownEditor"
import SearchResults from "../components/SearchResults"
import TaskCard from "../components/TaskCard"
import TaskEditor from "../components/TaskEditor"
import { useDebounce } from "../hooks/useDebounce"
import { trpc } from "../lib/trpc"

interface Project {
  id: string
  title: string
  description?: string
  strategyContent?: string
  parentId?: string
  userId: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  children: Project[]
  taskCount: number
}

interface Task {
  id: string
  content: string
  description?: string | null
  projectId: string
  assigneeId?: string | null
  status: "todo" | "in_progress" | "done"
  priority: number
  estimatedMinutes?: number | null
  aiCategorized: boolean
  isWeeklyPriority?: boolean
  weekAssigned?: string | null
  createdAt: Date
  updatedAt: Date
  // New fields for hierarchy display
  projectTitle?: string
  projectDescription?: string
  // Assignee info
  assigneeName?: string | null
  assigneeEmail?: string | null
  assigneeAvatarUrl?: string | null
  // Labels
  labels?: {
    id: string | null
    name: string | null
    color: string | null
    description?: string | null
  }[]
}

interface Milestone {
  id: string
  title: string
  description?: string | null
  projectId: string
  targetDate?: Date | null
  completedDate?: Date | null
  isCompleted: boolean
  createdAt: Date
  updatedAt: Date
}

interface StagedTask {
  id: string
  content: string
  projectId?: string | null
}

// Multi-select component for filters
interface MultiSelectProps {
  options: { value: string; label: string }[]
  selectedValues: string[]
  onChange: (values: string[]) => void
  placeholder: string
  className?: string
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value))
    } else {
      onChange([...selectedValues, value])
    }
  }

  const removeOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selectedValues.filter((v) => v !== value))
  }

  const getSelectedLabels = () => {
    return selectedValues.map((value) => options.find((opt) => opt.value === value)?.label || value)
  }

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-background cursor-pointer min-h-[40px] flex items-center gap-1 flex-wrap"
      >
        {selectedValues.length === 0 ? (
          <span className="text-gray-500">{placeholder}</span>
        ) : (
          <>
            {getSelectedLabels().map((label, index) => {
              const value = selectedValues[index]
              if (!value) return null
              return (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                >
                  {label}
                  <button onClick={(e) => removeOption(value, e)} className=" hover:text-blue-800">
                    ×
                  </button>
                </span>
              )
            })}
          </>
        )}
        <span className="ml-auto text-gray-400">{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-gray-300 rounded-md shadow-lg z-20 max-h-48 overflow-y-auto">
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className={`px-3 py-2 cursor-pointer text-sm  flex items-center gap-2 ${
                  selectedValues.includes(option.value) ? "bg-blue-50 text-blue-700" : ""
                }`}
              >
                <input type="checkbox" checked={selectedValues.includes(option.value)} readOnly className="rounded " />
                {option.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const getCurrentWeekStart = () => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const sunday = new Date(today)
  sunday.setDate(today.getDate() - dayOfWeek)
  sunday.setHours(0, 0, 0, 0)
  return sunday
}

const BrainScreen: React.FC = () => {
  const navigate = useNavigate()
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [editorMode, setEditorMode] = useState<"project" | "task" | null>(null)
  const [showCreateProject, setShowCreateProject] = useState(false)
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [showCreateMilestone, setShowCreateMilestone] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null)

  // Labels management state
  const [showLabelsModal, setShowLabelsModal] = useState(false)
  const [editingLabel, setEditingLabel] = useState<any | null>(null)

  // Bulk actions state
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [lastSelectedTaskIndex, setLastSelectedTaskIndex] = useState<number | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("")
  const [projectFilter, setProjectFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<("todo" | "in_progress" | "done")[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Project queries
  const {
    data: hierarchyData,
    isLoading: hierarchyLoading,
    refetch: refetchHierarchy,
  } = trpc.brain.getProjectHierarchy.useQuery()

  // New query for flat project list (for dropdown)
  const { data: allProjectsData, isLoading: allProjectsLoading } = trpc.brain.getAllProjects.useQuery()

  // Debug query for project relationships
  const { data: relationshipsData, refetch: refetchRelationships } = trpc.brain.getProjectRelationships.useQuery()

  // Fix hierarchy mutation
  const fixHierarchyMutation = trpc.brain.fixProjectHierarchy.useMutation({
    onSuccess: () => {
      void refetchHierarchy()
      void refetchRelationships()
      alert("Hierarchy fixed successfully!")
    },
  })

  // Auto-select first project if none selected and projects exist
  if (!selectedProjectId && allProjectsData?.projects && allProjectsData.projects.length > 0) {
    setSelectedProjectId(allProjectsData.projects[0]?.id)
  }

  // Project-specific tasks query only
  const {
    data: tasksData,
    isLoading: tasksLoading,
    refetch: refetchTasks,
  } = trpc.brain.getProjectTasks.useQuery({ projectId: selectedProjectId }, { enabled: !!selectedProjectId })

  // Apply client-side filtering to tasks
  const filteredTasks = useMemo(() => {
    if (!tasksData?.tasks) return []

    let filtered = tasksData.tasks

    // Apply project filter
    if (projectFilter.length > 0) {
      filtered = filtered.filter((task) => projectFilter.includes(task.projectId))
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((task) => statusFilter.includes(task.status as "todo" | "in_progress" | "done"))
    }

    return filtered
  }, [tasksData?.tasks, projectFilter, statusFilter])

  // Milestones query
  const {
    data: milestonesData,
    isLoading: milestonesLoading,
    refetch: refetchMilestones,
  } = trpc.brain.getMilestones.useQuery({ projectId: selectedProjectId }, { enabled: !!selectedProjectId })

  // Search query - only enabled for search, not used for main task display filtering
  const { data: searchData, isLoading: searchLoading } = trpc.brain.searchContent.useQuery(
    {
      query: debouncedSearchQuery,
      projectFilter: undefined, // Don't use project filter for search
      statusFilter: undefined, // Don't use status filter for search
    },
    {
      enabled: debouncedSearchQuery.length >= 2,
      keepPreviousData: true,
    },
  )

  // Get raw tasks and apply client-side filtering
  const rawTasks = tasksData?.tasks || []
  const tasks = rawTasks.filter((task) => {
    // Apply project filter
    if (projectFilter.length > 0 && !projectFilter.includes(task.projectId)) {
      return false
    }

    // Apply status filter
    if (statusFilter.length > 0 && !statusFilter.includes(task.status as "todo" | "in_progress" | "done")) {
      return false
    }

    return true
  })

  // Create models list from MODEL_METADATA
  const getAvailableModels = () => {
    const llmModels = Object.entries(MODEL_METADATA)
      .filter(([_, metadata]: any) => metadata.modelType === ModelType.LLM)
      .map(([modelId, metadata]: any) => ({
        id: modelId,
        name: metadata.displayName,
        provider: getProviderFromModelId(modelId),
        category: getCategoryFromModelId(modelId),
      }))

    return llmModels
  }

  const getProviderFromModelId = (modelId: string): string => {
    if (modelId.includes("google/gemini")) return "Google"
    if (modelId.includes("deepseek")) return "DeepSeek"
    if (modelId.includes("gpt-")) return "OpenAI"
    if (modelId.includes("claude")) return "Anthropic"
    if (modelId.includes("qwen")) return "Qwen"
    return "Unknown"
  }

  const getCategoryFromModelId = (modelId: string): string => {
    if (modelId.includes(GeminiModelType.GEMINI_2_5_FLASH)) return "recommended"
    if (modelId.includes("deepseek-r1")) return "reasoning"
    if (modelId.includes("deepseek-chat")) return "fast"
    if (modelId.includes("free")) return "free"
    return "standard"
  }

  // Helper function to find a project by ID in the hierarchy
  const findProjectById = (projectId: string, projects: Project[]): Project | null => {
    for (const project of projects) {
      if (project.id === projectId) return project
      if (project.children?.length > 0) {
        const found = findProjectById(projectId, project.children)
        if (found) return found
      }
    }
    return null
  }

  // Helper function to get project breadcrumbs from hierarchy
  const getProjectBreadcrumbs = (projectId: string, projects: Project[]): { id: string; title: string }[] => {
    const breadcrumbs: { id: string; title: string }[] = []

    const findProjectPath = (
      projects: Project[],
      targetId: string,
      path: { id: string; title: string }[] = [],
    ): boolean => {
      for (const project of projects) {
        const currentPath = [...path, { id: project.id, title: project.title }]

        if (project.id === targetId) {
          breadcrumbs.push(...currentPath)
          return true
        }

        if (project.children.length > 0) {
          if (findProjectPath(project.children, targetId, currentPath)) {
            return true
          }
        }
      }
      return false
    }

    findProjectPath(projects, projectId)
    return breadcrumbs
  }

  // Mutations
  const createProjectMutation = trpc.brain.createProject.useMutation({
    onSuccess: () => {
      void refetchHierarchy()
      setShowCreateProject(false)
    },
  })

  const updateProjectMutation = trpc.brain.updateProject.useMutation({
    onSuccess: () => {
      void refetchHierarchy()
      setEditingProject(null)
    },
  })

  const deleteProjectMutation = trpc.brain.deleteProject.useMutation({
    onSuccess: () => {
      void refetchHierarchy()
      // If we deleted the currently selected project, reset selection
      if (selectedProjectId) {
        setSelectedProjectId(null)
      }
    },
  })

  // Create project conversation mutation
  // Navigate to AI Chat
  const navigateToAIChat = () => {
    navigate({ to: "/ai-chat" })
  }

  const createTaskMutation = trpc.brain.createTask.useMutation({
    onSuccess: () => {
      void refetchTasks()
      void refetchHierarchy() // Update task counts
      setShowCreateTask(false)
    },
  })

  const updateTaskStatusMutation = trpc.brain.updateTaskStatus.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const archiveTaskMutation = trpc.brain.archiveTask.useMutation({
    onSuccess: () => {
      void refetchTasks()
      void refetchHierarchy() // Update task counts
    },
  })

  const setWeeklyPriorityMutation = trpc.brain.setTaskWeeklyPriority.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const bulkSetWeeklyPrioritiesMutation = trpc.brain.bulkSetWeeklyPriorities.useMutation({
    onSuccess: () => {
      void refetchTasks()
      setSelectedTaskIds(new Set())
      setShowBulkActions(false)
    },
  })

  const bulkArchiveTasksMutation = trpc.brain.bulkArchiveTasks.useMutation({
    onSuccess: () => {
      void refetchTasks()
      void refetchHierarchy() // Update task counts
      setSelectedTaskIds(new Set())
      setShowBulkActions(false)
    },
  })

  const updateProjectStrategyMutation = trpc.brain.updateProjectStrategy.useMutation({
    onSuccess: () => {
      void refetchHierarchy()
    },
  })

  const updateTaskDescriptionMutation = trpc.brain.updateTaskDescription.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const reorderTasksMutation = trpc.brain.reorderTasks.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  // Milestone mutations
  const createMilestoneMutation = trpc.brain.createMilestone.useMutation({
    onSuccess: () => {
      void refetchMilestones()
    },
  })

  const updateMilestoneMutation = trpc.brain.updateMilestone.useMutation({
    onSuccess: () => {
      void refetchMilestones()
    },
  })

  const deleteMilestoneMutation = trpc.brain.deleteMilestone.useMutation({
    onSuccess: () => {
      void refetchMilestones()
    },
  })

  // Task labels and time mutations for TaskCard
  const updateTaskEstimatedTimeMutation = trpc.brain.updateTaskEstimatedTime.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const updateTaskPriorityMutation = trpc.brain.updateTaskPriority.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const updateTaskStatusByTaskIdMutation = trpc.brain.updateTaskStatusByTaskId.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const assignTaskLabelMutation = trpc.brain.assignTaskLabel.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  const removeTaskLabelMutation = trpc.brain.removeTaskLabel.useMutation({
    onSuccess: () => {
      void refetchTasks()
    },
  })

  // Task labels query
  const { data: availableLabels, refetch: refetchLabels } = trpc.brain.getTaskLabels.useQuery()

  // Label management mutations
  const createLabelMutation = trpc.brain.createTaskLabel.useMutation({
    onSuccess: () => {
      void refetchLabels()
      setEditingLabel(null)
    },
  })

  const updateLabelMutation = trpc.brain.updateTaskLabel.useMutation({
    onSuccess: () => {
      void refetchLabels()
      setEditingLabel(null)
    },
  })

  const deleteLabelMutation = trpc.brain.deleteTaskLabel.useMutation({
    onSuccess: () => {
      void refetchLabels()
      void refetchTasks() // Refresh tasks to update labels
    },
  })

  const projects = hierarchyData?.projects || []
  const allProjects = allProjectsData?.projects || []
  const milestones = milestonesData?.milestones || []
  const currentTasksLoading = tasksLoading
  const models = getAvailableModels()

  // Helper to calculate total task count including children
  const getTotalTaskCount = (projects: Project[]): number => {
    let total = 0

    const countTasks = (projectList: Project[]) => {
      for (const project of projectList) {
        total += project.taskCount || 0
        // Safely handle children array
        const children = Array.isArray(project.children) ? project.children : []
        if (children.length > 0) {
          countTasks(children)
        }
      }
    }

    countTasks(projects)
    return total
  }

  // Keyboard shortcuts using react-hotkeys-hook
  useHotkeys("ctrl+backspace,cmd+backspace", () => {
    if (selectedTaskIds.size > 0) {
      handleBulkDelete()
    }
  }, [selectedTaskIds])

  useHotkeys("escape", () => {
    if (selectedTaskIds.size > 0) {
      setSelectedTaskIds(new Set())
      setShowBulkActions(false)
      setLastSelectedTaskIndex(null)
    }
  }, [selectedTaskIds])

  const handleDeleteProject = (projectId: string) => {
    if (confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
      deleteProjectMutation.mutate({ id: projectId })
    }
  }

  const handleArchiveTask = (taskId: string) => {
    if (confirm("Are you sure you want to archive this task?")) {
      archiveTaskMutation.mutate({ id: taskId })
    }
  }

  // Drag & Drop handlers
  const handleTaskDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedProjectId) return

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex) return

    // Create a stable copy of tasks to avoid mutation issues
    const currentTasks = [...tasks]

    // Reorder tasks locally for immediate feedback
    const [removed] = currentTasks.splice(sourceIndex, 1)
    if (removed) {
      currentTasks.splice(destinationIndex, 0, removed)
    }

    // Get the new order of task IDs (extract ID from draggable ID if needed)
    const newTaskOrder = currentTasks.map((task) => task.id)

    // Update backend
    reorderTasksMutation.mutate({
      projectId: selectedProjectId,
      taskIds: newTaskOrder,
    })
  }

  // Milestone handlers
  const handleDeleteMilestone = (milestoneId: string) => {
    if (confirm("Are you sure you want to delete this milestone?")) {
      deleteMilestoneMutation.mutate({ id: milestoneId })
    }
  }

  const handleToggleMilestone = (milestoneId: string, isCompleted: boolean) => {
    updateMilestoneMutation.mutate({
      id: milestoneId,
      isCompleted: !isCompleted,
    })
  }

  const handleEditProjectStrategy = (projectId: string) => {
    setSelectedProjectId(projectId)
    setEditorMode("project")
    setSelectedTaskId(null)
    // Clear any bulk selection when opening editor
    setSelectedTaskIds(new Set())
    setShowBulkActions(false)
  }

  const handleEditTaskDescription = (taskId: string) => {
    setSelectedTaskId(taskId)
    setEditorMode("task")
    // Clear any bulk selection when opening editor
    setSelectedTaskIds(new Set())
    setShowBulkActions(false)
  }

  const handleCloseEditor = () => {
    setEditorMode(null)
    setSelectedTaskId(null)
    // Keep bulk selection if it exists
    if (selectedTaskIds.size === 0) {
      setShowBulkActions(false)
    }
  }

  const handleSaveProjectStrategy = (strategyContent: string) => {
    if (selectedProjectId) {
      updateProjectStrategyMutation.mutate({
        id: selectedProjectId,
        strategyContent,
      })
    }
  }

  const handleSaveTaskDescription = (description: string) => {
    if (selectedTaskId) {
      updateTaskDescriptionMutation.mutate({
        id: selectedTaskId,
        description,
      })
    }
  }

  const handleUpdateTaskPriority = (taskId: string, priority: number) => {
    updateTaskPriorityMutation.mutate({
      taskId,
      priority,
    })
  }

  const handleUpdateTaskStatus = (taskId: string, status: "todo" | "in_progress" | "done") => {
    updateTaskStatusByTaskIdMutation.mutate({
      taskId,
      status,
    })
  }

  const handleUpdateEstimatedTime = (taskId: string, minutes: number | null) => {
    updateTaskEstimatedTimeMutation.mutate({
      taskId,
      estimatedMinutes: minutes,
    })
  }

  // Search handlers
  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setShowSearchResults(query.length >= 2)
  }

  const handleSelectProject = (projectId: string) => {
    setSelectedProjectId(projectId)
    setShowSearchResults(false)
    setSearchQuery("")
  }

  const handleProjectChange = (taskId: string, newProjectId: string) => {
    // The mutation is already handled by the ProjectSelector component
    // Refetch tasks to update the UI
    void refetchTasks()
    void refetchHierarchy() // Update task counts in project hierarchy
    console.log(`Task ${taskId} moved to project ${newProjectId}`)
  }

  const handleSelectTask = (taskId: string) => {
    // Find the task to get its project ID
    if (searchData?.tasks) {
      const task = searchData.tasks.find((t) => t.id === taskId)
      if (task) {
        setSelectedProjectId(task.projectId)
      }
    }
    setShowSearchResults(false)
    setSearchQuery("")
  }

  const handleCloseSearch = () => {
    setShowSearchResults(false)
  }

  // Bulk actions handlers
  const handleTaskSelection = (
    taskId: string,
    isSelected: boolean,
    event?: React.ChangeEvent<HTMLInputElement>,
    taskIndex?: number,
  ) => {
    const newSelection = new Set(selectedTaskIds)

    if (
      event?.nativeEvent &&
      (event.nativeEvent as any).shiftKey &&
      lastSelectedTaskIndex !== null &&
      taskIndex !== undefined
    ) {
      // Shift-click: select range
      const start = Math.min(lastSelectedTaskIndex, taskIndex)
      const end = Math.max(lastSelectedTaskIndex, taskIndex)

      for (let i = start; i <= end; i++) {
        const task = tasks[i]
        if (task) {
          newSelection.add(task.id)
        }
      }
      setLastSelectedTaskIndex(taskIndex)
    } else {
      // Cmd/Ctrl-click and regular click both toggle individual selection
      if (isSelected) {
        newSelection.add(taskId)
      } else {
        newSelection.delete(taskId)
      }
      setLastSelectedTaskIndex(taskIndex ?? null)
    }

    setSelectedTaskIds(newSelection)
    setShowBulkActions(newSelection.size > 0)
  }

  // Handle task click for editing and multi-selection
  const handleTaskClick = (taskId: string, event: React.MouseEvent) => {
    event.stopPropagation()

    if (event.metaKey || event.ctrlKey) {
      // Multi-select with Cmd/Ctrl
      const newSelection = new Set(selectedTaskIds)
      if (newSelection.has(taskId)) {
        newSelection.delete(taskId)
      } else {
        newSelection.add(taskId)
      }
      setSelectedTaskIds(newSelection)
      setShowBulkActions(newSelection.size > 0)
    } else {
      // Single select - always enable editor mode for single task selection
      setSelectedTaskIds(new Set([taskId]))
      setShowBulkActions(false)
      setSelectedTaskId(taskId)
      setEditorMode("task")
    }
  }

  const handleSelectAllTasks = () => {
    if (selectedTaskIds.size === filteredTasks.length) {
      setSelectedTaskIds(new Set())
      setShowBulkActions(false)
    } else {
      setSelectedTaskIds(new Set(filteredTasks.map((task) => task.id)))
      setShowBulkActions(true)
    }
  }

  const handleBulkWeeklyPriority = (isWeeklyPriority: boolean) => {
    if (selectedTaskIds.size === 0) return

    bulkSetWeeklyPrioritiesMutation.mutate({
      taskIds: Array.from(selectedTaskIds),
      isWeeklyPriority,
      weekStart: getCurrentWeekStart(),
    })
  }

  const handleBulkDelete = () => {
    if (selectedTaskIds.size === 0) return

    const taskCount = selectedTaskIds.size
    const taskText = taskCount === 1 ? "task" : "tasks"

    if (confirm(`Are you sure you want to delete ${taskCount} ${taskText}? This action cannot be undone.`)) {
      bulkArchiveTasksMutation.mutate({
        taskIds: Array.from(selectedTaskIds),
      })
    }
  }

  // Helper function to format time
  const formatEstimatedTime = (minutes?: number) => {
    if (!minutes) return null
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  // Helper function to get priority color
  const getPriorityColor = (priority: number) => {
    if (priority >= 8) return "text-destructive bg-red-50"
    if (priority >= 6) return "text-orange-600 bg-orange-50"
    if (priority >= 4) return "text-yellow-600 bg-yellow-50"
    return " bg-foregroundx"
  }

  const renderProject = (project: Project, level = 0) => {
    const isSelected = selectedProjectId === project.id
    const indentStyle = { marginLeft: `${level * 16}px` }

    // Safely handle children array
    const children = Array.isArray(project.children) ? project.children : []

    return (
      <div key={project.id} className="mb-2">
        <div
          className={`p-3 rounded-lg border cursor-pointer transition-all ${
            isSelected ? "border-blue-500 bg-blue-50" : "border-border hover:border-gray-300 "
          }`}
          style={indentStyle}
          onClick={() => setSelectedProjectId(project.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium  truncate">{project.title}</h3>
              {project.description && <p className="text-sm  truncate mt-1">{project.description}</p>}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500">{project.taskCount} tasks</span>
                {children.length > 0 && <span className="text-xs ">{children.length} sub-projects</span>}
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigateToAIChat()
                }}
                className="text-gray-400 hover:text-green-600 p-1"
                title="Chat with AI"
              >
                💬
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingProject(project)
                }}
                className="text-gray-400 hover: p-1"
                title="Edit project"
              >
                ✏️
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteProject(project.id)
                }}
                className="text-gray-400 hover:text-destructive p-1"
                title="Delete project"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        {/* Render children safely */}
        {children.length > 0 && <div className="ml-4">{children.map((child) => renderProject(child, level + 1))}</div>}
      </div>
    )
  }

  const renderTask = (task: Task, index: number) => {
    // Get full breadcrumb path for this task
    const taskBreadcrumbs = getProjectBreadcrumbs(task.projectId, projects)
    const isFromDifferentProject = task.projectId !== selectedProjectId

    return (
      <TaskCard
        key={task.id}
        task={{
          ...task,
          status: task.status,
        }}
        index={index}
        selectedTaskIds={selectedTaskIds}
        projects={projects}
        draggableId={`task-${task.id}`}
        isDragDisabled={!selectedProjectId}
        isFromDifferentProject={isFromDifferentProject}
        taskBreadcrumbs={taskBreadcrumbs}
        onTaskClick={handleTaskClick}
        onTaskSelection={handleTaskSelection}
        onEditTaskDescription={handleEditTaskDescription}
        onArchiveTask={handleArchiveTask}
        onProjectSelect={setSelectedProjectId}
        onProjectChange={handleProjectChange}
        onStatusChange={(taskId, status) => {
          updateTaskStatusMutation.mutate({ id: taskId, status })
        }}
        onWeeklyPriorityToggle={(taskId, isWeeklyPriority, weekStart) => {
          const getWeekStart = () => {
            if (weekStart) return weekStart
            const today = new Date()
            const dayOfWeek = today.getDay()
            const sunday = new Date(today)
            sunday.setDate(today.getDate() - dayOfWeek)
            sunday.setHours(0, 0, 0, 0)
            return sunday
          }
          setWeeklyPriorityMutation.mutate({
            taskId,
            isWeeklyPriority,
            weekStart: getWeekStart(),
          })
        }}
        onPriorityChange={(taskId, priority) => {
          updateTaskPriorityMutation.mutate({ taskId, priority })
        }}
        onAssigneeChange={(taskId, assigneeId) => {
          // Handle assignee change - this will be handled by the AssigneeSelector's internal mutation
          // But we should refetch the tasks to update the UI
          void refetchTasks()
        }}
        onEstimatedTimeChange={(taskId, estimatedMinutes) => {
          // Handle estimated time updates - this will be handled by the TaskCard's internal mutation
        }}
        onLabelsChange={(taskId, labelIds) => {
          // Handle label changes - this will be handled by the TaskCard's internal mutations
        }}
      />
    )
  }

  // Clear filters function
  const clearFilters = () => {
    setProjectFilter([])
    setStatusFilter([])
    setSearchQuery("")
  }

  return (
    <div className="h-screen flex flex-col bg-foregroundx">
      {/* Header with Project Selector */}
      <div className="bg-background border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold ">🧠 Brain</h1>

            {/* Project Selector Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-primary">Project:</label>
              <select
                value={selectedProjectId || ""}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-background min-w-[200px]"
              >
                <option value="">🌍 Global View (All Projects)</option>
                {allProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLabelsModal(true)}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              🏷️ Manage Labels
            </button>
            <button
              onClick={() => setShowCreateProject(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              + New Project
            </button>
            {selectedProjectId && (
              <>
                <button
                  onClick={() => setShowCreateTask(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  + New Task
                </button>
                <button
                  onClick={() => setShowCreateMilestone(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  + Milestone
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters Row */}
      <div className="bg-background border-b border-border p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[300px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">🔍</span>
              </div>
              <input
                type="text"
                placeholder="Search projects and tasks..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("")
                    setShowSearchResults(false)
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Search Results */}
            {showSearchResults && searchData && (
              <SearchResults
                results={{
                  projects: searchData.projects.map((p) => ({
                    ...p,
                    type: "project" as const,
                  })),
                  tasks: searchData.tasks.map((t) => ({
                    ...t,
                    type: "task" as const,
                    status: t.status as "todo" | "in_progress" | "done",
                    estimatedMinutes: t.estimatedMinutes || undefined,
                  })),
                  totalResults: searchData.totalResults,
                }}
                searchQuery={debouncedSearchQuery}
                onSelectProject={handleSelectProject}
                onSelectTask={handleSelectTask}
                onClose={handleCloseSearch}
              />
            )}
          </div>

          {/* Project Filter */}
          <MultiSelect
            options={allProjects.map((project) => ({
              value: project.id,
              label: project.displayName || project.title,
            }))}
            selectedValues={projectFilter}
            onChange={setProjectFilter}
            placeholder="Filter by Project"
            className="min-w-[200px]"
          />

          {/* Status Filter */}
          <MultiSelect
            options={[
              { value: "todo", label: "Todo" },
              { value: "in_progress", label: "In Progress" },
              { value: "done", label: "Done" },
            ]}
            selectedValues={statusFilter}
            onChange={(values) => setStatusFilter(values as ("todo" | "in_progress" | "done")[])}
            placeholder="Filter by Status"
            className="min-w-[150px]"
          />

          {/* Clear Filters */}
          {(projectFilter.length > 0 || statusFilter.length > 0 || searchQuery) && (
            <button
              onClick={() => {
                setProjectFilter([])
                setStatusFilter([])
              }}
              className="px-3 py-2 text-sm  hover:text-gray-800 border border-gray-300 rounded-md "
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Compact Project Hierarchy Sidebar */}
        <div className="w-80 border-r border-border bg-background flex flex-col">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold ">Project Hierarchy</h2>
            <p className="text-sm  mt-1">{getTotalTaskCount(projects)} total tasks</p>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {hierarchyLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading projects...</div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">No projects yet</div>
                <button onClick={() => setShowCreateProject(true)} className=" hover:text-blue-700 font-medium">
                  Create your first project
                </button>
              </div>
            ) : (
              <div className="space-y-2">{projects.map((project) => renderProject(project, 0))}</div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Tasks Section */}
          <div className={`${editorMode ? "flex-1" : "flex-1"} flex flex-col overflow-hidden`}>
            {/* Selected Project Info or Global Header */}
            <div className="bg-background border-b border-border p-4">
              {selectedProjectId ? (
                (() => {
                  const selectedProject = findProjectById(selectedProjectId, projects)
                  const breadcrumbs = getProjectBreadcrumbs(selectedProjectId, projects)

                  return (
                    <div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {breadcrumbs.map((breadcrumb, index) => (
                              <React.Fragment key={breadcrumb.id}>
                                <span className="font-medium ">{breadcrumb.title}</span>
                                {index < breadcrumbs.length - 1 && <span className="text-gray-400">›</span>}
                              </React.Fragment>
                            ))}
                          </div>
                          {selectedProject?.description && <p className=" text-sm">{selectedProject.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm ">
                            {tasksData?.tasks.length || 0} tasks • {milestones.length} milestones
                          </span>
                          <button
                            onClick={() => handleEditProjectStrategy(selectedProjectId)}
                            className="text-gray-400 hover:text-green-600 p-1"
                            title="Edit project strategy"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => setEditingProject(selectedProject)}
                            className="text-gray-400 hover: p-1"
                            title="Edit project"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteProject(selectedProjectId)}
                            className="text-gray-400 hover:text-destructive p-1"
                            title="Delete project"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })()
              ) : (
                <div>
                  <h2 className="text-xl font-bold ">🧠 Brain</h2>
                  <p className=" text-sm">Select a project to view and manage tasks</p>
                </div>
              )}
            </div>

            {/* Milestones Section (only for selected project) */}
            {selectedProjectId && milestones.length > 0 && (
              <div className="bg-background border-b border-border p-4">
                <h3 className="font-medium  mb-3">🎯 Milestones</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {milestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className={`p-3 rounded-lg border ${
                        milestone.isCompleted ? "bg-green-50 border-green-200" : "bg-background border-border"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleMilestone(milestone.id, milestone.isCompleted)}
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                milestone.isCompleted
                                  ? "bg-green-600 border-green-600 text-white"
                                  : "border-gray-300 hover:border-green-400"
                              }`}
                            >
                              {milestone.isCompleted && "✓"}
                            </button>
                            <h4 className={`font-medium ${milestone.isCompleted ? "line-through text-gray-500" : ""}`}>
                              {milestone.title}
                            </h4>
                          </div>
                          {milestone.description && <p className="text-sm  mt-1">{milestone.description}</p>}
                          {milestone.targetDate && (
                            <p className="text-xs text-gray-500 mt-1">
                              Due: {new Date(milestone.targetDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingMilestone(milestone)}
                            className="text-gray-400 hover: p-1"
                            title="Edit milestone"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteMilestone(milestone.id)}
                            className="text-gray-400 hover:text-destructive p-1"
                            title="Delete milestone"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bulk Actions Bar */}
            {showBulkActions && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mx-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-800 font-medium">
                      {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? "s" : ""} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBulkWeeklyPriority(true)}
                      disabled={bulkSetWeeklyPrioritiesMutation.isLoading}
                      className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 disabled:opacity-50"
                    >
                      ⭐ Add to Weekly Priorities
                    </button>
                    <button
                      onClick={() => handleBulkWeeklyPriority(false)}
                      disabled={bulkSetWeeklyPrioritiesMutation.isLoading}
                      className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                    >
                      Remove from Weekly Priorities
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={bulkArchiveTasksMutation.isLoading}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                      title="Delete selected tasks (Cmd+Backspace)"
                    >
                      🗑️ Delete Tasks
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTaskIds(new Set())
                        setShowBulkActions(false)
                      }}
                      className="px-3 py-1 bg-gray-400 text-white text-sm rounded 0"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tasks Section with Drag & Drop */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Tasks Header with Select All */}
              {filteredTasks.length > 0 && (
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="text-lg font-semibold ">
                    {selectedProjectId
                      ? `Project Tasks (${filteredTasks.length})`
                      : `All Tasks (${filteredTasks.length})`}
                  </h3>
                  <button onClick={handleSelectAllTasks} className="text-sm  hover:text-blue-700 font-medium">
                    {selectedTaskIds.size === filteredTasks.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
              )}

              {!selectedProjectId ? (
                <div className="text-center py-16">
                  <div className="text-gray-500 mb-4">
                    <div className="text-6xl mb-4">🧠</div>
                    <h3 className="text-xl font-medium text-primary mb-2">Welcome to Brain</h3>
                    <p className="text-gray-500 mb-4">
                      Select a project from the sidebar to view and manage tasks, or create a new project to get
                      started.
                    </p>
                    <button
                      onClick={() => setShowCreateProject(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      + Create New Project
                    </button>
                  </div>
                </div>
              ) : currentTasksLoading ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading tasks...</div>
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    {tasksData?.tasks.length === 0
                      ? "No tasks in this project yet"
                      : "No tasks match the current filters"}
                  </div>
                  {tasksData?.tasks.length === 0 && (
                    <button onClick={() => setShowCreateTask(true)} className=" hover:text-blue-700 font-medium">
                      Create your first task
                    </button>
                  )}
                </div>
              ) : (
                // Drag & Drop enabled for project view
                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <Droppable droppableId="tasks">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-3 ${snapshot.isDraggingOver ? "bg-blue-50 rounded-lg p-2" : ""}`}
                      >
                        {filteredTasks.map((task, index) =>
                          renderTask({ ...task, status: task.status as "todo" | "in_progress" | "done" }, index),
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>

          {/* Markdown Editor Section */}
          {editorMode && (
            <div className="w-1/2 border-l border-border bg-background flex flex-col">
              <div className="flex-1 p-4">
                {editorMode === "project"
                  ? (() => {
                      const selectedProject = findProjectById(selectedProjectId, projects)
                      return (
                        <MarkdownEditor
                          value={selectedProject?.strategyContent || ""}
                          onSave={handleSaveProjectStrategy}
                          placeholder="Write your project strategy here..."
                          title={`Strategy for ${selectedProject?.title}`}
                          height={600}
                        />
                      )
                    })()
                  : editorMode === "task"
                    ? (() => {
                        const selectedTask = filteredTasks.find((t) => t.id === selectedTaskId)
                        if (!selectedTask) return null
                        return (
                          <TaskEditor
                            key={selectedTaskId} // Force re-render when task changes
                            task={{
                              id: selectedTask.id,
                              content: selectedTask.content,
                              description: selectedTask.description || undefined,
                              priority: selectedTask.priority,
                              estimatedMinutes: selectedTask.estimatedMinutes || undefined,
                              status: selectedTask.status as "todo" | "in_progress" | "done",
                            }}
                            onSave={handleSaveTaskDescription}
                            onUpdatePriority={(priority) => handleUpdateTaskPriority(selectedTask.id, priority)}
                            onUpdateEstimatedTime={(minutes) => handleUpdateEstimatedTime(selectedTask.id, minutes)}
                            onUpdateStatus={(status) => handleUpdateTaskStatus(selectedTask.id, status)}
                            onClose={handleCloseEditor}
                          />
                        )
                      })()
                    : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateProject && (
        <ProjectModal
          onClose={() => setShowCreateProject(false)}
          onSubmit={(data) => createProjectMutation.mutate(data)}
          projects={projects}
          isLoading={createProjectMutation.isLoading}
          selectedProjectId={selectedProjectId} // Pass selected project for auto-population
        />
      )}

      {editingProject && (
        <ProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSubmit={(data) => updateProjectMutation.mutate({ id: editingProject.id, ...data })}
          projects={projects}
          isLoading={updateProjectMutation.isLoading}
        />
      )}

      {showCreateTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold  mb-4">Create New Task</h3>
            <p className="text-sm  mb-4">Describe your task and it will be added to the selected project.</p>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                const content = formData.get("content") as string
                const priority = parseInt(formData.get("priority") as string)
                const estimatedMinutes = formData.get("estimatedMinutes") as string
                const isWeeklyPriority = formData.get("isWeeklyPriority") === "on"

                // Create the task first
                createTaskMutation.mutate(
                  {
                    content,
                    projectId: selectedProjectId,
                    priority,
                    estimatedMinutes: estimatedMinutes ? parseInt(estimatedMinutes) : undefined,
                  },
                  {
                    onSuccess: (newTask) => {
                      // If weekly priority is checked, set it after creation
                      if (isWeeklyPriority && newTask) {
                        setWeeklyPriorityMutation.mutate({
                          taskId: newTask.id,
                          isWeeklyPriority: true,
                          weekStart: getCurrentWeekStart(),
                        })
                      }
                    },
                  },
                )
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Task Description *</label>
                <textarea
                  name="content"
                  rows={3}
                  required
                  placeholder="e.g., Implement user authentication with JWT tokens"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Priority (0-4)</label>
                <input
                  name="priority"
                  type="number"
                  min="1"
                  max="10"
                  defaultValue="5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Higher numbers = higher priority (affects drag & drop order)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Estimated Time (minutes)</label>
                <input
                  name="estimatedMinutes"
                  type="number"
                  min="1"
                  placeholder="e.g., 120 for 2 hours"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Helps with time planning and workload estimation</p>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    name="isWeeklyPriority"
                    type="checkbox"
                    className="rounded border-gray-300  focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-primary">⭐ Add to this week&apos;s priorities</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">This task will appear in Weekly Planning for this week</p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateTask(false)}
                  className="px-4 py-2 text-primary border border-gray-300 rounded-lg  transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Milestone Create/Edit Modal */}
      {(showCreateMilestone || editingMilestone) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold  mb-4">
              {editingMilestone ? "Edit Milestone" : "Create New Milestone"}
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                const title = formData.get("title") as string
                const description = formData.get("description") as string
                const targetDate = formData.get("targetDate") as string
                if (editingMilestone) {
                  updateMilestoneMutation.mutate({
                    id: editingMilestone.id,
                    title,
                    description: description || undefined,
                    targetDate: targetDate ? new Date(targetDate) : undefined,
                  })
                } else {
                  createMilestoneMutation.mutate({
                    title,
                    description: description || undefined,
                    projectId: selectedProjectId,
                    targetDate: targetDate ? new Date(targetDate) : undefined,
                  })
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Title *</label>
                <input
                  name="title"
                  type="text"
                  required
                  defaultValue={editingMilestone?.title || ""}
                  placeholder="e.g., Launch MVP"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  defaultValue={editingMilestone?.description || ""}
                  placeholder="Milestone description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Target Date</label>
                <input
                  name="targetDate"
                  type="date"
                  defaultValue={
                    editingMilestone?.targetDate
                      ? new Date(editingMilestone.targetDate).toISOString().split("T")[0]
                      : ""
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateMilestone(false)
                    setEditingMilestone(null)
                  }}
                  className="px-4 py-2 text-primary border border-gray-300 rounded-lg  transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingMilestone ? "Update Milestone" : "Create Milestone"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Labels Management Modal */}
      {showLabelsModal && (
        <LabelsManagementModal
          labels={
            availableLabels?.labels.map((label) => ({
              id: label.id,
              name: label.name,
              color: label.color,
              description: label.description || undefined,
            })) || []
          }
          onClose={() => {
            setShowLabelsModal(false)
            setEditingLabel(null)
          }}
          onCreateLabel={(data) => createLabelMutation.mutate(data)}
          onUpdateLabel={(data) => updateLabelMutation.mutate(data)}
          onDeleteLabel={(id) => deleteLabelMutation.mutate({ id })}
          editingLabel={editingLabel}
          setEditingLabel={setEditingLabel}
          isLoading={createLabelMutation.isLoading || updateLabelMutation.isLoading || deleteLabelMutation.isLoading}
        />
      )}

      {/* BrainChatbox - Fixed at bottom */}
      <BrainChatbox selectedProjectId={selectedProjectId} />
    </div>
  )
}

// Project Modal Component
interface ProjectModalProps {
  project?: Project
  onClose: () => void
  onSubmit: (data: any) => void
  projects: Project[]
  isLoading: boolean
  selectedProjectId?: string | null // New prop for auto-population
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  project,
  onClose,
  onSubmit,
  projects,
  isLoading,
  selectedProjectId,
}) => {
  const [formData, setFormData] = useState({
    title: project?.title || "",
    description: project?.description || "",
    strategyContent: project?.strategyContent || "",
    parentId: project?.parentId || (selectedProjectId && !project ? selectedProjectId : ""), // Auto-populate parent
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const submitData = {
      ...formData,
      parentId: formData.parentId || undefined,
    }
    onSubmit(submitData)
  }

  // Flatten projects for parent selection
  const flattenProjects = (projects: Project[], level = 0): (Project & { level: number })[] => {
    const result: (Project & { level: number })[] = []
    for (const proj of projects) {
      result.push({ ...proj, level })
      result.push(...flattenProjects(proj.children, level + 1))
    }
    return result
  }

  const flatProjects = flattenProjects(projects).filter((p) => p.id !== project?.id)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4">{project ? "Edit Project" : "Create New Project"}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Strategy Content (Markdown)</label>
            <textarea
              value={formData.strategyContent}
              onChange={(e) => setFormData({ ...formData, strategyContent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={5}
              placeholder="Enter strategic context in markdown format..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Parent Project</label>
            <select
              value={formData.parentId}
              onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">No Parent (Root Level)</option>
              {flatProjects.map((proj) => (
                <option key={proj.id} value={proj.id}>
                  {"  ".repeat(proj.level)}
                  {proj.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-primary rounded-lg  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
            >
              {isLoading ? "Saving..." : project ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Task Modal Component
interface TaskModalProps {
  projectId: string
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}

const TaskModal: React.FC<TaskModalProps> = ({ projectId, onClose, onSubmit, isLoading }) => {
  const [content, setContent] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ content, projectId })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4">Create New Task</h3>
        <p className="text-sm  mb-4">Describe your task and AI will automatically calculate its strategic alignment.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Task Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="Describe what needs to be done..."
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-primary rounded-lg  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !content.trim()}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors"
            >
              {isLoading ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Labels Management Modal Component
interface LabelsManagementModalProps {
  labels: {
    id: string
    name: string
    color: string
    description?: string
  }[]
  onClose: () => void
  onCreateLabel: (data: { name: string; color: string; description?: string }) => void
  onUpdateLabel: (data: { id: string; name?: string; color?: string; description?: string }) => void
  onDeleteLabel: (id: string) => void
  editingLabel: any | null
  setEditingLabel: (label: any | null) => void
  isLoading: boolean
}

const LabelsManagementModal: React.FC<LabelsManagementModalProps> = ({
  labels,
  onClose,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  editingLabel,
  setEditingLabel,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    color: "#3B82F6",
    description: "",
  })

  // Predefined color options
  const colorOptions = [
    "#3B82F6", // Blue
    "#EF4444", // Red
    "#10B981", // Green
    "#F59E0B", // Yellow
    "#8B5CF6", // Purple
    "#F97316", // Orange
    "#06B6D4", // Cyan
    "#84CC16", // Lime
    "#EC4899", // Pink
    "#6B7280", // Gray
  ]

  // Set form data when editing
  React.useEffect(() => {
    if (editingLabel) {
      setFormData({
        name: editingLabel.name || "",
        color: editingLabel.color || "#3B82F6",
        description: editingLabel.description || "",
      })
    } else {
      setFormData({
        name: "",
        color: "#3B82F6",
        description: "",
      })
    }
  }, [editingLabel])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingLabel) {
      onUpdateLabel({
        id: editingLabel.id,
        ...formData,
      })
    } else {
      onCreateLabel(formData)
    }
    // Reset form
    setFormData({
      name: "",
      color: "#3B82F6",
      description: "",
    })
  }

  const handleDelete = (labelId: string) => {
    if (confirm("Are you sure you want to delete this label? This will remove it from all tasks.")) {
      onDeleteLabel(labelId)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold ">🏷️ Manage Labels</h3>
          <button onClick={onClose} className="text-gray-400 hover: text-2xl">
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Labels List */}
          <div>
            <h4 className="text-lg font-medium  mb-4">Existing Labels</h4>
            {labels.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No labels created yet. Create your first label!</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {labels.map((label) => (
                  <div
                    key={label.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg "
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: label.color }} />
                      <div className="flex-1">
                        <div className="font-medium ">{label.name}</div>
                        {label.description && <div className="text-sm ">{label.description}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingLabel(label)}
                        className=" hover:text-blue-700 p-1"
                        title="Edit label"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(label.id)}
                        className="text-destructive hover:text-destructive p-1"
                        title="Delete label"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create/Edit Form */}
          <div>
            <h4 className="text-lg font-medium  mb-4">{editingLabel ? "Edit Label" : "Create New Label"}</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., High Priority, Bug Fix, Feature"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Color *</label>
                <div className="flex gap-2 mb-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color ? "border-gray-800" : "border-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Optional description for this label..."
                />
              </div>

              <div className="flex gap-3">
                {editingLabel && (
                  <button
                    type="button"
                    onClick={() => setEditingLabel(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-primary rounded-lg  transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isLoading || !formData.name.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                >
                  {isLoading ? "Saving..." : editingLabel ? "Update Label" : "Create Label"}
                </button>
              </div>
            </form>

            {/* Label Preview */}
            {formData.name && (
              <div className="mt-4 p-3 bg-foregroundx rounded-lg">
                <div className="text-sm  mb-2">Preview:</div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm border">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: formData.color }} />
                  {formData.name}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrainScreen
